import React from 'react'
import type { CurrentGame } from '@/hooks/useCurrentGames'
import { BG, BORDER, TEXT, ACCENT, FONT, TABULAR, font } from '@/Components/Shell/tokens'
import { effectiveAwayColor, readableTeamColor } from '@/utils/colors'
import {
  Crest, MomentumFlame, InterestChip, SplitBar, CHIP_COLOR,
  RedZoneChip, inRedZone, type ChipKind,
} from './boardPieces'
import { FormatClock, FormatScore, leadingSide } from './gameFormat'
import type { ScoringModel } from '@/utils/displayScore'
import GaugePick, { type PickState } from './pickControl'

/**
 * SMALL density: four across, glanceable. ~286px wide and a uniform 179px tall, so 16
 * games land in roughly one screen.
 *
 * ⚠️ The header row carries a FIXED min-height. Grid rows stretch to their tallest cell,
 * so without it a card with no interest chip sits a few pixels shorter than its
 * neighbours and the row loses its baseline — a real defect in review.
 *
 * Deliberately carries no pick control and no last-play line. Both were removed as too
 * dense at this size; they live on the large card and in the modal.
 */

type Props = {
  game: CurrentGame
  chip: ChipKind | null
  pinned: boolean
  pinnedAccent: string
  scoringModel: ScoringModel
  onOpen: (id: number) => void
  /** See BoardCardLarge — null when picks do not apply. */
  pick?: PickState | null
}

const BoardCardSmall: React.FC<Props> = ({ game, chip, pinned, pinnedAccent, scoringModel, onOpen, pick }) => {
  const live = game.status === 'Active'
  const isFinal = game.status === 'Final'
  const home = game.homeTeam
  const away = game.awayTeam

  const homeScore = game.homeScore ?? 0
  const awayScore = game.awayScore ?? 0

  const awayFill = effectiveAwayColor(home?.color, away?.color, away?.secondaryColor, away?.tertiaryColor)
  const homeFill = home?.color || '#64748b'

  const homeWp = Math.round(game.homeWinProbability ?? 50)
  const awayWp = 100 - homeWp
  // Who is ahead is a FORMAT question — in frames it is frames won, not points.
  const leader = leadingSide(game)
  // Both sides, matching the large card (owner). The single favoured-side figure
  // that used to sit here made the reader do the subtraction to learn the other
  // half, and the swing counter beside it was a third number in a strip three
  // pixels tall. Applied per side so the pair cannot end up mismatched.
  const awayText = readableTeamColor(awayFill)
  const homeText = readableTeamColor(homeFill)

  const accent = pinned ? pinnedAccent : chip ? CHIP_COLOR[chip] : BORDER.hairline
  const possessionTeam = game.homeTeamPoss ? 'home' : game.awayTeamPoss ? 'away' : null
  const momentumMagnitude = Math.abs(game.momentum ?? 0)


  const teamRow = (side: 'away' | 'home', team: typeof home, score: number, ahead: boolean) => {
    const hasMomentum = live && momentumMagnitude > 0 && game.momentumTeam === team?.abbr
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
        <Crest teamId={team?.id} size={26} possession={live && possessionTeam === side} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...font(500, 10), color: TEXT.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {team?.city}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginTop: '2px', minWidth: 0 }}>
            <span style={{
              ...font(ahead ? 700 : 500, 15, 1, '-0.015em'),
              color: ahead ? TEXT.primary : TEXT.muted,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{team?.name}</span>
            <span style={{ ...font(500, 10), color: TEXT.muted, ...TABULAR, flexShrink: 0 }}>{team?.record}</span>
            {hasMomentum && <MomentumFlame magnitude={momentumMagnitude} size={12} />}
          </div>
        </div>
        <span style={{ flexShrink: 0 }}>
          <FormatScore
            game={game}
            side={side}
            scoringModel={scoringModel}
            size={26}
            color={ahead ? TEXT.primary : TEXT.muted}
          />
        </span>
      </div>
    )
  }

  return (
    <div
      className="plate"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(game.id)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(game.id) } }}
      style={{
        boxSizing: 'border-box',
        background: pinned ? BG.cardOwn : BG.card,
        border: `1px solid ${pinned ? BORDER.raised : BORDER.hairline}`,
        borderTop: `2px solid ${accent}`,
        padding: '15px 17px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        cursor: 'pointer',
        fontFamily: FONT,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minHeight: '18px' }}>
        {isFinal ? (
          <span style={{ ...font(700, 10, 1, '0.02em'), color: TEXT.muted, ...TABULAR }}>FINAL</span>
        ) : live ? (
          game.isHalftime
            ? <span style={{ ...font(700, 10, 1, '0.02em'), color: ACCENT.live, ...TABULAR }}>HALFTIME</span>
            : <FormatClock game={game} size="small" />
        ) : (
          <span style={{ ...font(700, 10, 1, '0.02em'), color: TEXT.muted, ...TABULAR }}>SCHEDULED</span>
        )}
        <span style={{ flex: 1 }} />
        {/* No format badge (owner) — the rules strip names the format once at the top. */}
        {/* Named on this density: the small card has no field position and no
            last play, so an unattributed RED ZONE would not say whose threat. */}
        {inRedZone(game) && <RedZoneChip abbr={game.possession} size="small" />}
        {chip && <InterestChip kind={chip} size="small" />}
      </div>

      {teamRow('away', away, awayScore, leader !== 'home')}
      {teamRow('home', home, homeScore, leader !== 'away')}

      {/* The gauge is a LIVE readout and nothing more. On a final it resolved to 100/0, and
          the margin that replaced it was removed (owner) — the two scores directly above
          already say who won and by how much, so the row was restating the card. Finals
          simply drop it rather than showing an empty strip. */}
      {!isFinal && (
        <div style={{ paddingTop: '12px', borderTop: `1px solid ${BORDER.hairline}`, display: 'flex', alignItems: 'center', gap: '9px' }}>
          {/* Same control as the large card, one size down — the two boards must not
              drift on what a pick looks like or where it lives. */}
          <GaugePick side="away" teamId={away?.id} abbr={away?.abbr} pct={awayWp}
                     favored={awayWp > homeWp} color={awayText} size={11} pick={pick} />
          <SplitBar awayPct={awayWp} awayColor={awayFill} homeColor={homeFill} height={3} />
          <GaugePick side="home" teamId={home?.id} abbr={home?.abbr} pct={homeWp}
                     favored={homeWp > awayWp} color={homeText} size={11} pick={pick} />
        </div>
      )}
    </div>
  )
}

export default BoardCardSmall
